import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./ListItemTitle.module.scss";
import { PT_TREE_NAME_BUTTON_TESTID } from "@/constants/test-ids";
import { TreeService } from "@/services";
import { getActiveItemFromUrl } from "@/utils";
import { IPanelScope, ITreeItem } from "@/interfaces";
import { FILTERS_TAB_KEY, validations } from "@/constants";
import { TreeInput } from "@/shared-components/TreeInput";
import { parseErrorMessages } from "@/utils/common";
import { HighlightText } from "@/shared-components";
import { ListTypes } from "@/enums";
import { FavoriteItem } from "@/types";

interface ListItemTitleProps {
  scope: IPanelScope;
  data: ITreeItem | FavoriteItem;
  name: string;
  isSelected: boolean;
  handleClickFilter: (
    e: React.MouseEvent,
    data: ITreeItem | FavoriteItem,
  ) => void;
  isOpen: boolean;
  type: ListTypes;
  criteria: string;
  isEditable: boolean;
  isDataReady: boolean;
  isHover?: boolean;
  handleRename?: (newName: string) => void;
}

function getExtraData(data: ITreeItem | FavoriteItem, isSelected: boolean) {
  if ("extra" in data) {
    return {
      icon: data?.extra?.getIcon?.(isSelected),
      paddingLeft: data.extra.paddingLeft ?? 0,
    };
  }
  return {
    icon: undefined,
    paddingLeft: 0,
  };
}

export const ListItemTitle = ({
  name,
  scope,
  data,
  type,
  isSelected,
  isEditable,
  criteria,
  isDataReady,
  isHover = false,
  handleClickFilter,
  handleRename,
}: ListItemTitleProps) => {
  const { icon, paddingLeft: marginLeft } = getExtraData(data, isSelected);
  const [maxWidth, setMaxWidth] = useState<number | undefined | string>(
    undefined,
  );
  const nameDivRef = useRef<HTMLDivElement>(null);
  const clickableAreaRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<string>(name);
  const [errors, setErrors] = useState<string[] | undefined>();
  const isFavoriteType = type == ListTypes.FAVORITES;
  const inputRef = useRef<HTMLInputElement>(null);
  const isFirstRun = useRef<boolean>(true);
  const filterFolderPanel = document.getElementById("filter_folder_panel");

  useEffect(() => {
    if (
      clickableAreaRef?.current &&
      isSelected &&
      isFirstRun.current &&
      isDataReady
    ) {
      isFirstRun.current = false;
      setTimeout(() => {
        clickableAreaRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);
    }
  }, [
    isDataReady,
    isSelected,
    clickableAreaRef?.current,
    clickableAreaRef?.current?.getBoundingClientRect().top,
  ]);

  useEffect(() => {
    if (inputRef.current && isEditable) inputRef.current.focus();
  }, [inputRef, isEditable]);

  useEffect(() => {
    setValue(name);
  }, [name]);

  useEffect(() => {
    let activeItemId = getActiveItemFromUrl(scope);

    if (!activeItemId) {
      activeItemId = TreeService.getActiveNode(scope)?.id;
    }
  }, [data]);

  useEffect(() => {
    calculateMaxWidth();
  }, [isHover]);

  const calculateMaxWidth = () => {
    if (filterFolderPanel && nameDivRef.current) {
      const leftOffset =
        filterFolderPanel.getBoundingClientRect().right -
        nameDivRef.current.getBoundingClientRect().left;
      const padding_hover = 55;
      const padding_default = 35;
      const padding = isHover ? padding_hover : padding_default;
      const newMaxWidth = leftOffset - padding;
      setMaxWidth(newMaxWidth);
    }
  };
  useLayoutEffect(() => {
    if (!clickableAreaRef.current || !filterFolderPanel) return;

    const observer = new ResizeObserver((entries) => {
      // Wrap in requestAnimationFrame to prevent layout thrashing
      window.requestAnimationFrame(() => {
        if (!entries?.[0]) return;
        calculateMaxWidth();
      });
    });

    observer.observe(filterFolderPanel);

    return () => {
      observer.disconnect(); // Use disconnect instead of unobserve to ensure cleanup
    };
  }, [clickableAreaRef]);

  const nameValidators =
    scope.tab === FILTERS_TAB_KEY
      ? validations.FILTER_NAME
      : validations.FOLDER_NAME;
  const rename = async (val: string) => {
    if (!handleRename) {
      return;
    }

    const newVal = val.trim();
    const currErrors = [];
    for (const validator of nameValidators) {
      if (!validator.regex.test(newVal)) {
        currErrors.push(validator.message);
      }
    }

    if (currErrors.length > 0) {
      setErrors(currErrors);
      return;
    }

    setErrors(undefined);
    try {
      handleRename(newVal);
      setValue(newVal);
    } catch (e) {
      console.error(`ListItemTitle->rename`, e);
      setErrors(parseErrorMessages(e));
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    handleClickFilter(e, data);
  };

  return (
    <div
      ref={clickableAreaRef}
      className={styles.clickableArea}
      id={`${isFavoriteType ? "favorite" : "filter"}_${data.id}`}
      onClick={handleClick}
      data-testid={PT_TREE_NAME_BUTTON_TESTID}
      role="button"
      tabIndex={0} // Make it focusable
      onKeyDown={() => {
        // Handle keyboard events
      }}
    >
      {icon && <span className={styles.icon}>{icon}</span>}
      <div
        ref={nameDivRef}
        className={styles.title}
        style={{
          marginLeft,
          ...(isEditable ? {} : { maxWidth }),
        }}
      >
        {isEditable ? (
          <TreeInput
            errors={errors}
            originalValue={value}
            saveHandler={rename}
          />
        ) : (
          <HighlightText fullText={name} text={value} criteria={criteria} />
        )}
      </div>
    </div>
  );
};
